import type { Block } from "../content/types";
import { guide as discord } from "./discord";
import { guide as facebook } from "./facebook";
import { guide as instagram } from "./instagram";
import { guide as mobileGames } from "./mobile-games";
import { guide as netflix } from "./netflix";
import { guide as pinterest } from "./pinterest";
import { guide as reddit } from "./reddit";
import { guide as safari } from "./safari";
import { guide as snapchat } from "./snapchat";
import { guide as tiktok } from "./tiktok";
import { guide as twitch } from "./twitch";
import { guide as twitter } from "./twitter";
import type { BlockGuide } from "./types";
import { guide as whatsapp } from "./whatsapp";
import { guide as youtube } from "./youtube";

/**
 * Every guide, in the order `/block` lists them.
 *
 * Roughly by how often the app comes up when somebody describes the problem
 * rather than by install base — which is why a video app most people have never
 * opened sits above a messaging app almost everybody has.
 */
export const BLOCK_GUIDES: BlockGuide[] = [
  discord,
  facebook,
  instagram,
  mobileGames,
  netflix,
  pinterest,
  reddit,
  safari,
  snapchat,
  tiktok,
  twitch,
  twitter,
  whatsapp,
  youtube,
];

export const BLOCK_GUIDES_BY_SLUG: Record<string, BlockGuide> =
  Object.fromEntries(BLOCK_GUIDES.map((g) => [g.slug, g]));

export function blockGuideBySlug(slug: string): BlockGuide | undefined {
  return BLOCK_GUIDES_BY_SLUG[slug];
}

/** The path a guide lives at. Nothing hand-writes one. */
export function blockPath(slug: string): string {
  return `/block/${slug}`;
}

/** Roughly how long the page takes to read. Counts the steps, which are read. */
export function blockMinutes(item: BlockGuide): number {
  const prose = item.body.reduce((n, block) => n + blockWords(block), 0);
  const around =
    item.mechanic.reduce((n, line) => n + count(line), 0) +
    count(item.limitFails) +
    item.instead.reduce((n, line) => n + count(line), 0) +
    item.takeaways.reduce((n, line) => n + count(line), 0) +
    item.verses.reduce(
      (n, pick) => n + count(pick.verse.text) + count(pick.note),
      0,
    ) +
    (item.faq ?? []).reduce((n, e) => n + count(e.q) + count(e.a), 0);
  return Math.max(1, Math.round((prose + around) / 220));
}

function count(text: string): number {
  return text.trim() ? text.trim().split(/\s+/).length : 0;
}

function blockWords(block: Block): number {
  switch (block.type) {
    case "ul":
    case "ol":
      return count(block.items.join(" "));
    case "verse":
      return count(block.verse.text);
    default:
      return count(block.text);
  }
}

/** The `<h2>`s of a guide's prose, for its table of contents. */
export function blockOutline(item: BlockGuide): { id: string; text: string }[] {
  return item.body.flatMap((block) =>
    block.type === "h2" ? [{ id: block.id, text: block.text }] : [],
  );
}

export type { BlockGuide } from "./types";
